import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const C = 299792458;

export default function LengthContractionCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Length Contraction Calculator", url: "https://calclab.app/calculator/length-contraction" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Length Contraction Calculator",
      "Calculate relativistic length contraction using L = L₀/γ. Free online special relativity calculator.",
      "https://calclab.app/calculator/length-contraction",
      lang
    )
  ]);
  const [properLength, setProperLength] = useState("");
  const [velocity, setVelocity] = useState("");
  const [result, setResult] = useState<number | null>(null);
  const [gamma, setGamma] = useState<number | null>(null);

  const calculateContraction = () => {
    const l0 = parseFloat(properLength);
    const v = parseFloat(velocity);

    if (isNaN(l0) || isNaN(v)) {
      toast.error("Please enter valid values");
      return;
    }

    if (v >= C) {
      toast.error("Velocity must be less than speed of light");
      return;
    }

    const beta = v / C;
    const gammaFactor = 1 / Math.sqrt(1 - beta * beta);
    const contractedLength = l0 / gammaFactor;

    setGamma(gammaFactor);
    setResult(contractedLength);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setProperLength("");
    setVelocity("");
    setResult(null);
    setGamma(null);
  };

  return (
    <PageLayout calculatorName="Length Contraction Calculator" calculatorPath="/calculator/length-contraction">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Length Contraction Calculator</h1>
          <p className="text-muted-foreground">
            Calculate relativistic length contraction
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: L = L₀/γ where γ = 1/√(1-v²/c²)</CardTitle>
            <CardDescription>
              Objects contract in direction of motion at high speeds
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="properLength">Proper Length L₀ (m)</Label>
                <Input
                  id="properLength"
                  type="number"
                  step="any"
                  placeholder="Length at rest"
                  value={properLength}
                  onChange={(e) => setProperLength(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="velocity">Velocity v (m/s)</Label>
                <Input
                  id="velocity"
                  type="number"
                  step="any"
                  placeholder="Speed of object"
                  value={velocity}
                  onChange={(e) => setVelocity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Speed of light c = 299,792,458 m/s
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateContraction} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && gamma !== null && (
              <>
                <div className="space-y-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Contracted Length</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(6)} m
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Length measured by stationary observer
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Contraction factor: {(result / parseFloat(properLength)).toFixed(6)}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Lorentz Factor (γ)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {gamma.toFixed(6)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        v/c = {(parseFloat(velocity) / C).toFixed(6)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["length-contraction"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Objects shrink in direction of motion</p>
                <p>• Contraction only along direction of motion</p>
                <p>• At 0.9c, length is ~44% of rest length</p>
                <p>• Proper length is maximum (at rest)</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
